import '../scss/main.scss';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/grid';

import { domReady } from './utils/domReady.js';
import { debounce } from './utils/debounce.js';
import {
  setHeaderHeight,
  initHeaderScroll,
  setProdFactsHeight,
  initSectionDividers,
  initSmoothAnchorScroll,
  initScrollToTop,
  initStickyButtons,
} from './utils/layout-utils.js';
import initNavbar from './modules/navbar.js';
import { initMenu } from './modules/menu.js';
import initSwiperSlider from './modules/slider.js';
import { initGalleryModal } from './modules/gallery-modal.js';
import initProductNavigation from './modules/product-navigation.js';
import initProductNavigationLegacy from './modules/product-navigation-legacy.js';
import { initFooterDrilldown } from './modules/footernav.js';
import initHotspots from './modules/hotspots.js';
// Flowbite wird ausschließlich über initTabs() gestartet. So werden fehlerhafte
// Tab-Ziele vorher bereinigt und können nicht die globale Initialisierung stoppen.
import initTabs from './modules/tabs.js';
import initIframeModal from './modules/iframe-modal.js';
import initLinksNav from './modules/links-nav.js';
import initImageRotator from './modules/image-rotator.js';
import initButtonAria from './modules/button-aria.js';
import initPopups from './modules/popup.js';

const renderDividers = initSectionDividers('.product-header__facts .section__wrapper');

// Measure header & facts in a robust way (after DOM, next frame, and after fonts if available)
async function stableInitialMeasure() {
  // 1) First paint settled
  await new Promise((r) => requestAnimationFrame(r));
  setHeaderHeight();
  setProdFactsHeight();

  // 2) After fonts ready (prevents Safari fresh-cache mis-measure)
  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch(_) {}
    setHeaderHeight();
    setProdFactsHeight();
  }

  // 3) After window load as a final safeguard
  await new Promise((r) => {
    if (document.readyState === 'complete') return r();
    window.addEventListener('load', r, { once: true });
  });
  setHeaderHeight();
  setProdFactsHeight();
}

domReady(async () => {
  // Debounced resize handlers (also re-render dividers)
  const remeasure = () => {
    setHeaderHeight();
    setProdFactsHeight();
    if (renderDividers) renderDividers();
  };
  const onResize = debounce(remeasure, 150);
  window.addEventListener('resize', onResize);
  window.addEventListener('scroll', remeasure, { passive: true });

  // Observe header element height changes (e.g., sticky states, banners) and update CSS var
  const headerEl = document.querySelector('.site-header');
  const announcementEl = document.querySelector('[data-announcement-bar]');
  if (headerEl && 'ResizeObserver' in window) {
    const ro = new ResizeObserver(() => {
      setHeaderHeight();
      if (renderDividers) renderDividers();
    });
    ro.observe(headerEl);
    if (announcementEl) {
      ro.observe(announcementEl);
    }
  }

  // Do an optimized, multi-stage initial measurement before wiring scroll/menu
  await stableInitialMeasure();

  // Now that we have a correct header height, install scroll behavior & navbar
  initHeaderScroll(50);
  initNavbar();

  // Menus should see the correct layout state
  const menu = initMenu();

  // UI modules
  initSwiperSlider();
  initTabs();
  initGalleryModal();
  initProductNavigation();
  initProductNavigationLegacy();
  initFooterDrilldown();
  initHotspots();
  initIframeModal();
  initStickyButtons();
  initLinksNav();
  initImageRotator();
  initButtonAria();
  initPopups();
  initScrollToTop();
  initSmoothAnchorScroll();

  // Initial render of section dividers (after correct measurements)
  if (renderDividers) renderDividers();

  console.log('JS-Module initialisiert ✅');
});
